import { FC, useId, useState } from 'react';
import { MdEdit } from 'react-icons/md';
import { fmtVal } from '../utils/debugPanelUtils';
import type { DirectWriteHandler } from '../FormInspector/directWrite.types';
import RequisiteEditor from './RequisiteEditor';
import { RELATED_ROLE_NAMES, ROLE_META, type IRelatedRequisite } from './fieldEditorUtils';

interface IRelatedRequisitesProps {
    items: IRelatedRequisite[];
    docId: string;
    isEditMode: boolean;
    directAvailable: boolean;
    onDirectWrite: DirectWriteHandler;
}

/** Реквизиты, питающие формулы поля: значение + роль (req/vis/ro/…), правятся тем же редактором. */
const RelatedRequisites: FC<IRelatedRequisitesProps> = ({ items, docId, isEditMode, directAvailable, onDirectWrite }) => {
    const [openKey, setOpenKey] = useState<string | null>(null);
    const editorId = useId();

    if (items.length === 0) return null;

    return (
        <div className="rf-field-formula-tooltip__section">
            <div className="rf-field-formula-tooltip__section-title">
                Связанные реквизиты <span className="rf-field-formula-tooltip__count">{items.length}</span>
            </div>
            <div className="rf-field-editor-list">
                {items.map((item) => {
                    const open = openKey === item.key;
                    return (
                        <div key={item.key}>
                            <button
                                type="button"
                                className={`rf-field-rel-row ${open ? 'is-open' : ''}`}
                                onClick={() => setOpenKey(open ? null : item.key)}
                                aria-expanded={open}
                                aria-controls={open ? editorId : undefined}
                                aria-label={`${open ? 'Свернуть редактор' : 'Редактировать'}: ${item.name}`}
                            >
                                <span className="rf-field-rel-row__identity">
                                    <span className="rf-field-rel-row__name" title={item.key}>{item.name}</span>
                                    <span className="rf-field-rel-row__roles">
                                        {item.roles.map((role) => {
                                            const meta = ROLE_META[role];
                                            return (
                                                <span
                                                    key={role}
                                                    className="rf-field-rel-row__role"
                                                    title={meta?.title ?? role}
                                                >
                                                    {RELATED_ROLE_NAMES[role] ?? role} <code>{meta?.label ?? role}</code>
                                                </span>
                                            );
                                        })}
                                    </span>
                                </span>
                                <span className={`rf-field-rel-row__value ${item.value == null || item.value === '' ? 'is-muted' : ''}`}>
                                    {item.value == null || item.value === '' ? 'Не задано' : fmtVal(item.value)}
                                </span>
                                <span className="rf-field-rel-row__edit"><MdEdit size={13} /></span>
                            </button>
                            {open && (
                                <div className="rf-field-rel-row__editor" id={editorId}>
                                    <code className="rf-field-rel-row__key" title={item.key}>{item.key}</code>
                                    <RequisiteEditor
                                        fieldKey={item.key}
                                        docId={docId}
                                        rhfPath={item.rhfPath}
                                        value={item.value}
                                        isEditMode={isEditMode}
                                        directAvailable={directAvailable}
                                        onDirectWrite={onDirectWrite}
                                        compact
                                    />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default RelatedRequisites;
